import type { OverviewSummary } from "@payrollmart-invoice/shared";
import { formatDollars } from "@payrollmart-invoice/shared";
import { Activity } from "lucide-react";

interface YtdStatsProps {
	overview: OverviewSummary;
}

/**
 * Year-to-date statistics block — revenue, gross profit, new customers, and
 * avg days to close, laid out as a 2x2 grid of stat tiles. All derived from
 * the already-fetched OverviewSummary payload; no extra round-trip.
 */
export function YtdStats({ overview }: YtdStatsProps) {
	const { ytd } = overview;
	const tiles = [
		{
			label: "Revenue billed",
			value: formatDollars(ytd.revenueCents),
			sub: `Across ${ytd.invoiceCount} ${ytd.invoiceCount === 1 ? "invoice" : "invoices"}`,
		},
		{
			label: "Gross profit",
			value: formatDollars(ytd.grossProfitCents),
			sub: `${ytd.marginPercent}% blended margin`,
		},
		{
			label: "New customers",
			value: String(ytd.newCustomers),
			sub: ytd.newCustomers === 0 ? "None added this year" : "Added this year",
		},
		{
			label: "Avg days to close",
			value: ytd.avgDaysToClose === null ? "—" : ytd.avgDaysToClose.toFixed(1),
			sub:
				ytd.avgDaysToClose === null ? "Nothing pushed yet this year" : "From cycle close to push",
		},
	];

	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white" data-testid="ytd-stats">
			<div className="flex items-center justify-between border-b border-[#e5e7eb] px-6 py-4">
				<div className="flex items-center gap-2">
					<Activity className="h-4 w-4 text-[#6b7280]" strokeWidth={1.5} />
					<h2 className="text-sm font-semibold text-[#1e1e20]">
						Year to date, {overview.yearLabel}
					</h2>
				</div>
			</div>
			<div className="grid gap-4 p-6 sm:grid-cols-2">
				{tiles.map((tile) => (
					<div key={tile.label}>
						<div className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#9ca3af]">
							{tile.label}
						</div>
						<div className="mt-1 text-[26px] font-semibold leading-none tracking-tight tabular-nums text-[#1e1e20]">
							{tile.value}
						</div>
						<div className="mt-1.5 text-[11.5px] text-[#6b7280]">{tile.sub}</div>
					</div>
				))}
			</div>
		</div>
	);
}
